import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import formatDateViewAll from "../../../utils/formatDateViewAll";
import TitleSubtitlePair from "../../../components/_utils/TitleSubtitlePair";
import TopNavSmallButton from "../../../components/_utils/CommonButtons/TopNavSmallButton";
import SwitcherTabs from "../../../components/_utils/CommonButtons/SwitcherTabs";
import CommonDetailCard from "../../../components/_utils/CommonDetailCard";
import CardActionableButton from "../../../components/_utils/CommonViewAllCard/CardSubComponent/CardActionableButton/index.jsx";
import CommonToast from "../../../components/_utils/CommonToast";
import ICONS from "../../../assets/Icons";
import { linkIdentifier } from "../../../utils/linkIdentifier";
import useCustomSearchParams from "../../../utils/useCustomSearchParams.js";
import Loader from "../../../components/Loader/Index";
import { toast } from "react-toastify";
import {
  errorcode,
  GlobalConfiguration,
  IntervalConfiguration,
  PaginationConfiguration,
} from "../../../utils/configurationProvider";
import {
  getSpecificChannel,
  resetNotificationsData,
  testChannel,
  updateChannelDetailData,
} from "../../../redux/features/Notifications";
import { channelEdit } from "../../../redux/features/Notifications";

import LargePopupRedirect from "../../../components/_utils/CommonPopups/CommonLargePopupRedirect/index.jsx";
import TopNavComponent from "../../../components/_utils/CommonTopNav/TopNavComponent/index.jsx";
import PaginationComponent from "../../../components/_utils/PaginationComponent/index.jsx";
import accountNameStandardised from "../../../utils/accountNamestandardised.js";
import ChannelViewAll from "../ChannelViewall/index.jsx";
import NotificationViewall from "../../Notifications/NotificationViewall/index.jsx";
import TEXT from "../../../utils/globalText.js";
import globalText from "../../../utils/globalText.js";

/**
 * @param {object} props
 * @param {string} props.channelId - Channel Id
 * @param {boolean} props.popupMode - Enable Popup mode of channel details
 * @returns {JSX.Element}
 */
const ChannelDetails = ({ channelId, popupMode }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  // Prefer an explicitly passed channelId (popup usage) over the route param.
  const selectedChannelId = channelId || id;

  // --- Redux state -----------------------------------------------------------
  const { getSpecificChannelData, notificationsData } = useSelector(
    (state) => state?.notificationsSlice,
  );
  const dropdownOptions = PaginationConfiguration.paginationOptions;

  // Synchronises pagination-related state with the URL's query string.
  // If we navigated here with `location.state.read` set (e.g. "deep link to
  // unread notifications"), seed the `type` filter param with that value.
  const { searchParam, handleSearchParams } = useCustomSearchParams(
    location?.state && location?.state?.read
      ? {
          type: location?.state?.read,
          pageno: GlobalConfiguration.lengthCheckOne,
          pagesize: dropdownOptions[GlobalConfiguration?.lengthCheckZero],
        }
      : {
          pageno: GlobalConfiguration.lengthCheckOne,
          pagesize: dropdownOptions[GlobalConfiguration?.lengthCheckZero],
        },
  );

  // --- Refs --------------------------------------------------------------
  const cooldownRef = useRef(false); // Prevents spamming the "test notification" action(simple client-side cooldown).
  const timerRef = useRef(null); // Holds the cooldown timer id so it can be cleared/reset.
  const copyTextRef = useRef(null); // Holds the "copied to clipboard" tooltip reset timer id.

  // --- Pagination state ----------------------------------------------------

  const [pageSize, setPageSize] = useState(
    searchParam.get(
      globalText?.PAGE?.toLowerCase() + globalText?.SIZE?.toLowerCase(),
    ) || PaginationConfiguration.paginationSmall, // Initialised from the URL search params (falls back to config defaults).
  );
  const [currentPage, setCurrentPage] = useState(
    searchParam.get(globalText?.PAGE?.toLowerCase() + "no") ||
      GlobalConfiguration.lengthCheckOne,
  );

  const services = useSelector((state) => state?.refreshToken?.services) || []; // List of service/permission flags the logged-in user has

  const [loading, setLoading] = useState(true); // Overall "channel details" loading flag

  const [isLoading, setIsLoading] = useState(false);

  const [isOpen, setIsOpen] = useState(false); // pagination page-size dropdown open state

  const [copytoolkitText, setCopyToolkitText] = useState(
    globalText?.SHOW?.toLowerCase(),
  ); // Controls whether the webhook URL is masked ("show") or revealed ("hide").

  // --- Tab visibility flags --------------------------------------------------
  const [showChannelDetails, setShowChannelDetails] = useState(true);

  // Currently selected target provider (gchat/teams/slack/generic) used when
  // opening the "related channels for this provider" popup.
  const [targetProvider, setTargetProvider] = useState("");
  const [tooltip, setTooltip] = useState(TEXT?.COPY_TO_CLIPBOARD);

  // Tracks the previously active tab index so the slide animation direction
  // (next vs prev) can be computed in the layout effect below.
  const [prevIndex, setPrevIndex] = useState(
    GlobalConfiguration?.lengthCheckZero,
  );
  // CSS class Applied to the active tab's content for the slide-in animation.
  const [animationClass, setAnimationClass] = useState("");

  // Disables the "Test notification" button while its cooldown is active.
  const [testNotificationBtnDisable, setTestNotificationBtnDisable] =
    useState(false);

  // Active tab index: lengthCheckZero = Details, lengthCheckOne = Notifications.
  const [index, setIndex] = useState(
    showChannelDetails
      ? GlobalConfiguration.lengthCheckZero
      : GlobalConfiguration?.lengthCheckOne,
  );

  const [isChannelViewallOpen, setIsChannelViewallOpen] = useState(false);
  const [isNotificationViewAllOpen, setIsNotificationViewAllOpen] =
    useState(false);

  // Currently selected "rows per page" value shown in the pagination dropdown.
  const [selectedValue, setSelectedValue] = useState(
    searchParam.get(
      globalText?.PAGE?.toLowerCase() + globalText?.SIZE?.toLowerCase(),
    ) || PaginationConfiguration.paginationSmall,
  );

  // Loading flag specific to the notifications list (separate from the
  // page-level `loading` flag so switching tabs doesn't re-show the big loader).
  const [notificationDataLoading, setNotificationDataLaoding] = useState(true);

  // True if the user has permission to activate/deactivate/edit the channel.
  const hasServices =
    services?.includes(globalText?.ADMIN) ||
    services?.includes(globalText?.VIEW_CHANNEL);

  // Scrollable container ref for the notification list (used to reset scroll
  // position on page change and as the IntersectionObserver root).
  const scrollContainerRef = useRef(null);

  /**
   * Entry point for the "Activate/Deactivate channel" button.
   * Blocks the action in popup mode or when the user lacks permissions,
   * otherwise routes to the activate or deactivate handler based on the
   * channel's current `is_active` state.
   */
  const handleChannelActivation = () => {
    if (popupMode) {
      actionRestricted();
    } else if (!hasServices) {
      navigate("/feature_not_assigned", {
        state: {
          unavailableService: `${globalText?.VIEW_CHANNEL}, ${globalText?.ADMIN}`,
        },
      });
    } else if (id) {
      if (getSpecificChannelData?.is_active) {
        handleDeactivatingChannel();
      } else {
        handleActivatingChannel();
      }
    }
  };

  /**
   * Calls the API to set `is_active: false` on the current channel, updates
   * the store on success, and surfaces an error toast on failure.
   */
  const handleDeactivatingChannel = async () => {
    setIsLoading(true);

    const isActive = false;

    try {
      const response = await dispatch(
        channelEdit({ id: selectedChannelId, isActive }),
      );

      if (response?.payload?.status) {
        // Push the updated channel object back into the store so the UI
        // reflects the new status immediately.
        await dispatch(
          updateChannelDetailData(
            response?.payload?.channels?.[GlobalConfiguration?.lengthCheckZero],
          ),
        );
      } else {
        // API rejected the change error message as a toast.
        setTimeout(() => {
          CommonToast({
            text: response?.payload?.base?.[
              GlobalConfiguration?.lengthCheckZero
            ],
            icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
            variant: "var(--error)",
            btnProps: {
              isDisabled: false,
              enabledTextColor: "var(--text-primary)",
              enabledBgColor: "var(--primary-bg)",
            },
          });
        }, IntervalConfiguration?.intervalMedium);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Calls the API to set `is_active: true` on the current channel, updates
   * the store on success, and surfaces an error toast on failure.
   */
  const handleActivatingChannel = async () => {
    if (!hasServices) {
      navigate("/feature_not_assigned", {
        state: {
          unavailableService: `${globalText?.VIEW_CHANNEL}, ${globalText?.ADMIN}`,
        },
      });
      return;
    }
    setIsLoading(true);

    const isActive = true;

    try {
      const response = await dispatch(
        channelEdit({ id: selectedChannelId, isActive }),
      );

      if (response?.payload?.status) {
        await dispatch(
          updateChannelDetailData(
            response?.payload?.channels?.[GlobalConfiguration?.lengthCheckZero],
          ),
        );
      } else {
        setTimeout(() => {
          CommonToast({
            text: response?.payload?.base?.[
              GlobalConfiguration?.lengthCheckZero
            ],
            icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
            variant: "var(--error)",
            btnProps: {
              isDisabled: false,
              enabledTextColor: "var(--text-primary)",
              enabledBgColor: "var(--primary-bg)",
            },
          });
        }, IntervalConfiguration?.intervalMedium);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Rows shown in the "Origin Details" card on the Details tab.
  let originItemList = [
    {
      content: getSpecificChannelData?.created_by_name
        ? getSpecificChannelData?.created_by_name
        : `${TEXT?.N_A}`,
      heading: `${TEXT?.CREATED_BY}`,
      type: "text",
    },

    {
      content: getSpecificChannelData?.created_at
        ? formatDateViewAll(getSpecificChannelData?.created_at, true, true)
        : `${TEXT?.N_A}`,
      heading: `${TEXT?.CREATED_ON}`,
      type: "text",
    },

    {
      content: getSpecificChannelData?.last_tested_at
        ? formatDateViewAll(getSpecificChannelData?.last_tested_at, true, true)
        : `${TEXT?.N_A}`,
      heading: `${TEXT?.NOTIFICATION_TESTED_ON}`,
      type: "text",
    },
  ];

  // Rows shown in the "Support Information" card on the Details tab
  // (currently just the channel id with a copy-to-clipboard action).
  let supportItemList = [
    {
      content: getSpecificChannelData?.id || globalText?.N_A?.toUpperCase(),
      heading: "Channel ID",
      type: "textWithButton",
      iconArray: [
        {
          onClick: (e) => handleCopyChannelId(),
          title: tooltip,
          icon:
            tooltip === globalText?.COPIED
              ? ICONS?.SELECTED_WITH_GREEN_CIRCLE
              : "",
        },
      ],
    },
  ];

  /**
   * Fetches the channel's own details (name, provider, status, etc).
   * Redirects to the not-found route (`navigate("*")`) if the API reports a 404.
 
   */
  const fetchChanneslDetails = async () => {
    try {
      const response = await dispatch(
        getSpecificChannel({
          id: selectedChannelId,
          page_size: pageSize,
          page_no: currentPage,
        }),
      );

      if (response?.payload?.status) {
        setLoading(false);
      }

      if (response?.payload?.status === errorcode?.errorNotFound) {
        navigate("*");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (key, value) => {
    handleSearchParams(key, value);
  };
  const handlePagination = (page) => {
    handleSubmit(globalText?.PAGE?.toLowerCase() + "no", page);

    setCurrentPage(page);
  };
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  /**
   * Applies a new page size, resets to page 1, and closes the dropdown.
   */
  const handleSelect = (value) => {
    handleSubmit(
      globalText?.PAGE?.toLowerCase() + globalText?.SIZE?.toLowerCase(),
      value,
    );

    setSelectedValue(value);
    setPageSize(value);
    setCurrentPage(GlobalConfiguration.lengthCheckOne);

    toggleDropdown();
  };

  /**
   * Switches to the "Details" tab.
   */
  const toggleDetails = () => {
    setShowChannelDetails(true);
  };

  /**
   * Maps a channel provider string (e.g. "slack") to its display icon.
   */
  const mapChannelIcon = (channelProvider) => {
    if (!channelProvider) {
      return;
    }
    let data = [
      { icon: ICONS.GOOGLE_CHAT, provider: "gchat" },
      { icon: ICONS.MICROSOFT_TEAMS, provider: "teams" },
      { icon: ICONS.WEB_HOOK, provider: "generic" },
      { icon: ICONS.SLACK, provider: "slack" },
    ]?.filter(
      (item) =>
        item?.provider?.toLowerCase?.()?.includes(channelProvider || "") || "",
    );
    return data?.[GlobalConfiguration?.lengthCheckZero]?.icon;
  };

  /**
   * Maps a channel provider string to a short, two-line display name (JSX)
   * used for compact buttons (e.g. top-nav action buttons).
   */
  const mapChannelName = (channelProvider) => {
    if (!channelProvider) {
      return;
    }
    const data = [
      {
        name: (
          <>
            Google <br /> chat
          </>
        ),
        provider: "gchat",
      },
      {
        name: (
          <>
            Microsoft <br /> teams
          </>
        ),
        provider: "teams",
      },
      { name: "Webhook", provider: "generic" },
      { name: "Slack", provider: "slack" },
    ].filter((item) =>
      item?.provider
        ?.toLowerCase?.()
        ?.includes(channelProvider?.toLowerCase?.() ?? ""),
    );

    return data?.[GlobalConfiguration?.lengthCheckZero]?.name;
  };

  /**
   * Same provider -> name mapping as `mapChannelName`, but returns a plain
   * single-line string
   */
  const mapChannelNameDefaultSearch = (channelProvider) => {
    if (!channelProvider) {
      return;
    }
    const data = [
      {
        name: "Google Chat",
        provider: "gchat",
      },
      {
        name: "Microsoft Teams",
        provider: "teams",
      },
      { name: "Webhook", provider: "generic" },
      { name: "Slack", provider: "slack" },
    ].filter((item) =>
      item?.provider
        ?.toLowerCase?.()
        ?.includes(channelProvider?.toLowerCase?.() ?? ""),
    );

    return data?.[GlobalConfiguration?.lengthCheckZero]?.name;
  };

  /**
   * Maps a raw linkage/verification status ("pending"/"verified"/"failed")
   * to a standardised display label (e.g. "Pending", "Success", "Failed").
   */
  const mapChannelLinkageStatus = (channelStatus) => {
    if (!channelStatus) {
      return;
    }

    let data = [
      {
        status: TEXT?.PENDING,
        linkageStatus: TEXT?.PENDING.toLowerCase(),
      },
      { status: TEXT?.SUCCESS?.toLowerCase(), linkageStatus: TEXT?.VERIFIED.toLowerCase() },
      {
        status: TEXT?.FAILED,
        linkageStatus: TEXT?.FAILED.toLowerCase(),
      },
    ]?.filter((itm) => {
      if (
        itm?.linkageStatus?.toLowerCase()?.includes(channelStatus || "") ||
        ""
      )
        return itm;
    });

    return accountNameStandardised(
      data?.[GlobalConfiguration?.lengthCheckZero]?.status,
    );
  };

  /**
   * Sends a test notification through the channel. Guards against:
   *  - popup mode (action restricted)
   *  - missing "CChannel" permission
   *  - inactive channel
   *  - a channel whose verification/linkage previously failed
   *  - spamming (10s client-side cooldown via `cooldownRef`)
   * On success, marks the channel as "verified" in the store.
   */
  const handleTestNotification = async () => {
    if (popupMode) {
      actionRestricted();
    } else if (!services?.includes("CChannel")) {
      navigate("/feature_not_assigned", {
        state: {
          unavailableService: "CChannel, Admin",
        },
      });
    } else if (!getSpecificChannelData?.is_active || popupMode) {
      return;
    } else if (id) {
      if (getSpecificChannelData?.verification_status === "failed") {
        toast.dismiss();
        CommonToast({
          text: "Channel linkage failed.",
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          btnProps: {
            isDisabled: false,
            enabledTextColor: "var(--text-primary)",
            enabledBgColor: "var(--primary-bg)",
          },
        });
        return;
      } else {
        if (cooldownRef.current) {
          toast.dismiss();
          CommonToast({
            text: TEXT?.TEST_MESSAGE_LIMIT_REACHED,
            icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
            variant: "var(--error)",
            btnProps: {
              isDisabled: false,
              enabledTextColor: "var(--text-primary)",
              enabledBgColor: "var(--primary-bg)",
            },
          });
          return;
        }
        // Start the cooldown before the request even resolves, so rapid
        // double-clicks can't slip through while awaiting the response.
        cooldownRef.current = true;
        setTestNotificationBtnDisable(true);

        clearTimeout(timerRef.current);

        timerRef.current = setTimeout(() => {
          cooldownRef.current = false;
          setTestNotificationBtnDisable(false);
        }, IntervalConfiguration.intervalTestNotification);

        CommonToast({
          text: "Sending test message.",
          icon: ICONS.LOADER_GIF,
          variant: "var(--processing)",
        });

        try {
          const response = await dispatch(testChannel({ id }));

          if (response?.payload?.status === true) {
            toast.dismiss();

            // A successful test message implies the linkage is now verified.
            const payloadData = {
              ...response?.payload,
              verification_status: "verified",
            };

            await dispatch(updateChannelDetailData(payloadData));

            CommonToast({
              text: TEXT?.TEST_MESSAGE_SENT_SUCCESSFULLY,
              icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
              variant: "var(--success)",
              btnProps: {
                isDisabled: false,
                enabledTextColor: "var(--text-primary)",
                enabledBgColor: "var(--primary-bg)",
              },
            });
          }
        } catch (error) {
          console.error(error);
        } finally {
        }
      }
    }
  };

  /**
   * Navigates to the "edit channel" page, subject to permission/popup checks.
   */
  const handleEditChannel = () => {
    if (popupMode) {
      actionRestricted();
    } else if (!hasServices) {
      navigate("/feature_not_assigned", {
        state: {
          unavailableService: `${globalText?.VIEW_CHANNEL}, ${globalText?.ADMIN}`,
        },
      });
    } else if (id) {
      navigate(
        `/home/notification_dashboard/channel_view_all/channel_detail_page/edit_channel/${selectedChannelId}`,
      );
    }
  };

  /**
   * Opens the "related channels for this provider" popup
   * (e.g. "all Slack channels").
   */
  const handleTargetPopup = async () => {
    if (popupMode) {
      actionRestricted();
      return;
    }
    setIsChannelViewallOpen(true);
    let providerParams = {};
    providerParams.provider = getSpecificChannelData?.provider;
  };

  /**
   * Opens the "all notifications of this channel" popup
   */

  const handleNotificationViewAllPopup = async () => {
    if (!canUseNotifyService) {
      navigate("/feature_not_assigned", {
        state: {
          unavailableService: "Notify",
        },
      });
      return;
    }

    if (getSpecificChannelData?.verification_status === "pending") {
      // Don't open popup if channel linkage is pending
      return;
    }

    if (popupMode) {
      actionRestricted();
      return;
    }
    setIsNotificationViewAllOpen(true);
  };

  /**
   * Copies the channel id to the clipboard and briefly shows a "Copied" tooltip.
   */
  const handleCopyChannelId = async () => {
    const webhookUrl = getSpecificChannelData?.id;
    try {
      await navigator.clipboard.writeText(webhookUrl);

      setTooltip(TEXT?.COPIED);

      clearTimeout(copyTextRef.current);

      copyTextRef.current = setTimeout(() => {
        setTooltip(TEXT?.COPY_TO_CLIPBOARD);
      }, IntervalConfiguration.intervalMedium);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    dispatch(resetNotificationsData());
    fetchChanneslDetails();
  }, [selectedChannelId]);

  // Reset the notification list's scroll position whenever the page or page
  // size changes, so the user always starts at the top of the new page.
  useEffect(() => {
    if (scrollContainerRef?.current) {
      scrollContainerRef?.current?.scrollTo({
        top: GlobalConfiguration?.lengthCheckZero,
      });
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    setShowChannelDetails(true);
    setIndex(GlobalConfiguration.lengthCheckZero);
    setNotificationDataLaoding(true);
  }, [getSpecificChannelData?.id]);

  useEffect(() => {
    if (getSpecificChannelData?.provider) {
      setTargetProvider(getSpecificChannelData?.provider);
    }
  }, [getSpecificChannelData?.provider]);

  // Determines the slide animation direction (next vs prev) whenever the
  // active tab index changes, based on whether we moved forward or backward.
  useLayoutEffect(() => {
    if (index !== prevIndex) {
      const direction =
        index > prevIndex ? "animate-slide-next" : "animate-slide-prev";

      setAnimationClass(direction);
      setPrevIndex(index);
    }
  }, [index]);

  // Whether the user is allowed to mark notifications as read (used both by
  // manual mark-read actions elsewhere and the auto-mark-read-on-view effect).
  const canUseNotifyService =
    services?.includes(globalText?.NOTIFY) ||
    services?.includes(globalText?.ADMIN);

  const actionRestricted = () => {
    toast.dismiss();
    CommonToast({
      text: TEXT?.ACTION_RESTRICTED,
      icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
      variant: "var(--error)",
      btnProps: {
        isDisabled: false,
        btnText: "Open in full view",
        icon: ICONS?.REDIRECT,
        enabledTextColor: "var(--text-primary)",
        enabledBgColor: "var(--negative-bg)",
        handleFunction: () => {
          navigate(
            `/home/notification_dashboard/channel_view_all/channel_detail_page/${selectedChannelId}`,
          );
        },
      },
      buttonNeeded: true,
    });
  };

  return (
    <div
      className={` ${popupMode ? "h-height-full" : "h-screen"} flex  flex-col  overflow-hidden overflow-x-hidden`}
      style={{
        scrollPaddingTop: "var(--padding-md)",
        scrollPaddingBottom: "var(--padding-md)",
      }}
    >
      <div
        className={`hidden  ${popupMode ? "hidden" : ""}Qmd:flex min-h-navbar-breadcrumb-height bg-primary-nav w-width-full`}
      ></div>
      {notificationDataLoading ||
        (notificationsData?.notifications?.length >
          GlobalConfiguration?.lengthCheckZero &&
          index === GlobalConfiguration?.lengthCheckOne && (
            <div
              className={`block
                bg-primary-nav
              ${popupMode ? "absolute" : "fixed"} w-full bottom-bottom-base   rounded-none h-pagination-height  z-z-index-fixed-ui`}
            >
              <PaginationComponent
                handleSelect={handleSelect}
                selectedValue={selectedValue}
                data={notificationsData}
                pageSize={pageSize}
                dropdownOptions={dropdownOptions}
                handlePagination={handlePagination}
                currentPage={currentPage}
                setIsOpen={setIsOpen}
                typePopup={popupMode}
              />
            </div>
          ))}

      {/* "Related channels for this provider" popup, opened via the target/provider button. */}
      {isChannelViewallOpen && (
        <div className="fixed top-top-base left-left-base w-screen h-screen z-z-index-modal bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <LargePopupRedirect
            // isscrollRequired={true}
            headerTitle="Target"
            headersubtext={`All channels related to ${mapChannelNameDefaultSearch(getSpecificChannelData?.provider) || "N/A"} are listed below.`}
            closeiconhandleFunction={() => {
              setIsChannelViewallOpen(false);
            }}
            isbottomSectionneed={false}
            bottomHeaderTextNeed={false}
          >
            <ChannelViewAll
              setIsChannelViewallOpen={setIsChannelViewallOpen}
              providerName={mapChannelNameDefaultSearch(
                getSpecificChannelData?.provider,
              )}
              targetProvider={targetProvider}
              setTargetProvider={setTargetProvider}
              popup={true}
              setLoadingDetail={setLoading}
            />
          </LargePopupRedirect>
        </div>
      )}

      {/* Notification-viewall popup, opened via the `view channel notifications` button. */}
      {isNotificationViewAllOpen && (
        <div className="fixed top-top-base left-left-base w-screen h-screen z-z-index-modal bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <LargePopupRedirect
            headerTitle={`${TEXT?.CHANNEL} ${TEXT?.NOTIFICATION.toLowerCase()}s`}
            headersubtext={`All notifications related to ${getSpecificChannelData?.name} are listed below.`}
            closeiconhandleFunction={() => {
              setIsNotificationViewAllOpen(false);
            }}
            isbottomSectionneed={false}
            bottomHeaderTextNeed={false}
          >
            <NotificationViewall
              providerName={mapChannelNameDefaultSearch(
                getSpecificChannelData?.provider,
              )}
              channelId={selectedChannelId}
              channelLinkageStatus={getSpecificChannelData?.verification_status}
              channelActiveStatus={getSpecificChannelData?.is_active}
              typepop={"popup"}
              flow={"channelDetailPage"}
            />
          </LargePopupRedirect>
        </div>
      )}

      {/* Full-page loading state while the channel's own details are being fetched. */}
      {loading ? (
        <div className="device-loader-container h-full m-auto justify-center items-center flex">
          <Loader />
        </div>
      ) : (
        <>
          <div
            className={` ${popupMode ? "hidden" : ""} w-width-full flex Qsmd:hidden z-z-index-fixed-ui  items-center bg-primary-nav-act justify-between`}
          >
            <TopNavComponent
              isFormattingNotRequired={true}
              topNavHeader={`${TEXT?.CHANNEL}`}
              topNavSubText={`${TEXT?.CHANNEL} ${TEXT?.DETAILS.toLowerCase()}`}
            />
          </div>

          {/* Main scrollable content area (also the IntersectionObserver root). */}
          <div
            className={`device-content-page relative  overflow-y-auto  flex-1 overflow-x-hidden`}
            ref={scrollContainerRef}
            style={{
              scrollPaddingTop: "var(--padding-md)",
              scrollPaddingBottom: "var(--padding-md)",
            }}
          >
            <div
              className={`device-container relative Qsmd:mt-margin-base Qsmd:static Qsmd:pt-padding-base `}
            >
              {/* ---- Header card: name, linkage status, status, webhook URL, provider ---- */}
              <div className="w-auto bg-primary-nav md:pt-padding-base">
                <div className="Qlg:w-max-content-width w-width-full mx-auto  pt-padding-base sm:mt-margin-base">
                  {/* Row 1: channel name + linkage status, with desktop action buttons
                      (Test notification / Edit channel) on the right. */}
                  <div className=" flex border-b border-solid border-primary-bg justify-center  mx-margin-md Qsmd:mx-margin-base ">
                    <div className="w-width-full flex flex-col Qsmd:flex-row Qsmd:py-padding-md  ">
                      <div className="accountName Qsmd:py-padding-base py-padding-md w-width-full Qmd:px-padding-sm  flex flex-col Qsmd:w-width-md gap-y-gap-xs whitespace-normal break-words justify-center Qsmd:border-border-base border-b-border border-primary-bg ">
                        <TitleSubtitlePair
                          title={getSpecificChannelData?.name || TEXT?.N_A}
                          subtitle={`${TEXT?.CHANNEL} ${TEXT?.NAME.toLowerCase()}`}
                          variant="md"
                        />
                      </div>
                      <div className="border-r border-solid border-primary-bg"></div>

                      <div className="accountStatus flex flex-col  w-width-full Qsmd:w-width-md gap-y-gap-xs Qsmd:pl-padding-md justify-center py-padding-md Qsmd:py-padding-base ">
                        <TitleSubtitlePair
                          isBold={
                            getSpecificChannelData?.verification_status
                              ? true
                              : false
                          }
                          title={
                            getSpecificChannelData?.verification_status ===
                              undefined ||
                            getSpecificChannelData?.verification_status === null
                              ? TEXT?.N_A
                              : mapChannelLinkageStatus(
                                  getSpecificChannelData?.verification_status,
                                )
                          }
                          subtitle={`${globalText?.CHANNEL} ${globalText?.LINKAGE?.toLowerCase()}`}
                          variant={"md"}
                          titleColor={
                            getSpecificChannelData?.verification_status ===
                            TEXT?.PENDING.toLowerCase()
                              ? TEXT?.PENDING.toLowerCase()
                              : getSpecificChannelData?.verification_status ===
                                  TEXT?.VERIFIED.toLowerCase()
                                ? TEXT?.SETTLED.toLowerCase()
                                : getSpecificChannelData?.verification_status ===
                                    TEXT?.FAILED.toLowerCase()
                                  ? TEXT?.FAILED.toLowerCase()
                                  : TEXT?.WHITE.toLowerCase()
                          }
                        />
                      </div>
                    </div>
                    <div className=" flex-row  bg-primary-nav-act hidden Qsmd:flex ">
                      <TopNavSmallButton
                        imgColor="var(--text-primary)"
                        btnText={
                          <>
                            {TEXT?.TEST} <br />{" "}
                            {TEXT?.NOTIFICATION.toLowerCase()}
                          </>
                        }
                        icon={ICONS?.TEST_NOTIFICATION}
                        hoverOption={"white"}
                        enabledBgColor={"var(--primary-nav-act)"}
                        enabledTextColor={"var(--text-primary)"}
                        disabledBgColor={"var(--primary-nav)"}
                        disabledTextColor={"var(--text-primary)"}
                        isDisabled={
                          !services?.includes("CChannel") ||
                          !getSpecificChannelData?.is_active ||
                          testNotificationBtnDisable ||
                          popupMode
                        }
                        handleFunction={handleTestNotification}
                      />

                      <div className="border-r border-solid border-primary-bg my-margin-md"></div>
                      <TopNavSmallButton
                        btnText={
                          <>
                            {TEXT?.EDIT} <br /> {TEXT?.CHANNEL.toLowerCase()}
                          </>
                        }
                        icon={ICONS?.EDIT_WITH_CIRCLE}
                        hoverOption={"white"}
                        enabledBgColor={"var(--primary-nav-act)"}
                        imgColor={"var(--text-primary)"}
                        enabledTextColor={"var(--text-primary)"}
                        disabledBgColor={"var(--primary-nav)"}
                        disabledTextColor={"var(--text-primary)"}
                        isDisabled={!hasServices || popupMode}
                        handleFunction={handleEditChannel}
                      />
                    </div>
                  </div>

                  {/* Row 2: Active/Inactive status, with the desktop Activate/Deactivate button and View channel notification button. */}
                  <div className=" flex border-b border-solid border-primary-bg justify-center mx-margin-md Qsmd:mx-margin-base ">
                    <div className="  w-width-full flex flex-col  Qsmd:flex-row  Qsmd:py-padding-md">
                      <div className="accountName Qmd:pl-padding-sm Qsmd:py-padding-base flex flex-col w-width-full Qsmd:w-width-md gap-y-gap-xs whitespace-normal break-words justify-center py-padding-md Qsmd:border-b-border-base border-primary-bg ">
                        <TitleSubtitlePair
                          isBold={
                            getSpecificChannelData?.is_active === undefined ||
                            getSpecificChannelData?.is_active === null
                              ? false
                              : true
                          }
                          title={
                            getSpecificChannelData?.is_active === undefined ||
                            getSpecificChannelData?.is_active === null
                              ? TEXT?.N_A
                              : getSpecificChannelData.is_active
                                ? TEXT?.ACTIVE
                                : TEXT?.INACTIVE
                          }
                          subtitle={TEXT?.STATUS}
                          variant="md"
                          titleColor={
                            getSpecificChannelData?.is_active === undefined ||
                            getSpecificChannelData?.is_active === null
                              ? TEXT?.WHITE.toLowerCase()
                              : getSpecificChannelData.is_active
                                ? TEXT?.SETTLED.toLowerCase()
                                : TEXT?.FAILED.toLowerCase()
                          }
                        />
                      </div>
                    </div>
                    <div className=" hidden Qsmd:flex bg-primary-nav-act flex-row  ">
                      <div className="w-positive-negative-btn-width bg-primary-nav h-positive-negative-btn-height"></div>
                      <TopNavSmallButton
                        btnText={
                          <>
                            {getSpecificChannelData?.is_active
                              ? TEXT?.DEACTIVATE
                              : TEXT?.ACTIVATE}
                            <br /> {TEXT?.CHANNEL.toLowerCase()}
                          </>
                        }
                        icon={
                          getSpecificChannelData?.is_active
                            ? ICONS?.REJECT_ICON_RED_WITH_WHITE_CIRCLE
                            : ICONS?.APPROVE_CIRCLE_GREEN_CHECKMARK
                        }
                        hoverOption={"white"}
                        enabledTextColor={"var(--text-primary)"}
                        disabledTextColor={"var(--text-primary)"}
                        disabledBgColor={"var(--primary-nav)"}
                        isDisabled={!hasServices || isLoading || popupMode}
                        enabledBgColor={"var(--primary-nav-act)"}
                        handleFunction={handleChannelActivation}
                      />
                      <div className="border-r border-solid border-primary-bg my-margin-md"></div>
                      <TopNavSmallButton
                        btnText={
                          <>
                            {TEXT?.CHANNEL} <br />{" "}
                            {TEXT?.NOTIFICATION.toLowerCase()}s
                          </>
                        }
                        icon={ICONS?.NOTIFICATION_WITH_WHITE_CIRCLE}
                        hoverOption={"white"}
                        enabledBgColor={"var(--primary-nav-act)"}
                        enabledTextColor={"var(--text-primary)"}
                        disabledBgColor={"var(--primary-nav)"}
                        disabledTextColor={"var(--text-primary)"}
                        isDisabled={
                          !canUseNotifyService ||
                          getSpecificChannelData?.verification_status ===
                            "pending" ||
                          popupMode
                        }
                        imgColor={"var(--text-primary)"}
                        handleFunction={handleNotificationViewAllPopup}
                      />
                    </div>
                  </div>

                  {/* Row 3 (last row): webhook URL with reveal/hide toggle + copy,
                      and the desktop provider/target button. */}
                  <div className=" flex justify-center mx-margin-md Qsmd:mx-margin-base gap-gap-md ">
                    <div className="  w-width-full flex flex-col  Qsmd:flex-row  Qsmd:py-padding-md">
                      <div className="Qmd:pl-padding-sm Qsmd:py-padding-base flex flex-col w-width-full Qsmd:w-width-md gap-y-gap-xs whitespace-normal break-words justify-center py-padding-md Qsmd:border-b-border-base border-primary-bg flex-1 ">
                        <div className="flex items-start  gap-gap-sm">
                          <div className="flex flex-col gap-y-gap-xs  items-start">
                            <div className="flex">
                              <p
                                lang="en"
                                className="text-font-md text-text-primary flex-1 min-w-0 text-left font-normal [&_a]:!no-underline [&_a]:!text-inherit [&_a]:!cursor-default [&_a]:pointer-events-none"
                                style={{
                                  hyphens: "auto",
                                  WebkitHyphens: "auto",
                                  MozHyphens: "auto",
                                  msHyphens: "auto",
                                  overflowWrap: "anywhere",
                                  wordBreak: "normal",
                                  minWidth: GlobalConfiguration.lengthCheckZero,
                                }}
                              >
                                {/* Shows a generic placeholder while masked ("show" state),
                                    or the actual webhook URL (auto-linkified) once revealed. */}
                                {linkIdentifier(
                                  copytoolkitText ===
                                    globalText?.SHOW?.toLowerCase()
                                    ? `${TEXT?.WEBHOOOK} ${TEXT?.URL.toUpperCase()} ${TEXT?.CONFIGURED.toLowerCase()}`
                                    : getSpecificChannelData?.webhook_url,
                                )}
                              </p>
                            </div>
                            <div className="flex gap-gap-xs items-center">
                              <p className="text-font-xs text-text-secondary">
                                {TEXT?.CHANNEL} {TEXT?.URL.toUpperCase()}
                              </p>
                            </div>
                          </div>
                          {/* Webhook URL flips the
                              reveal/hide toggle. */}
                          <CardActionableButton
                            handleFunction={async () => {
                              try {
                                const webhookUrl =
                                  getSpecificChannelData?.webhook_url;

                                await navigator.clipboard.writeText(webhookUrl);

                                setCopyToolkitText(
                                  copytoolkitText ===
                                    globalText?.SHOW?.toLowerCase()
                                    ? globalText?.HIDE?.toLowerCase()
                                    : globalText?.SHOW?.toLowerCase(),
                                );
                              } catch (error) {
                                console.error(error);
                              }
                            }}
                            icon={
                              copytoolkitText ===
                              globalText?.SHOW?.toLowerCase()
                                ? ICONS.PASSWORD_VISIBLE_LIGHTGREY
                                : ICONS.PASSWORD_HIDDEN_LIGHTGREY
                            }
                            title={copytoolkitText}
                            bgColor={"var(--primary-nav-act)"}
                          />
                        </div>
                      </div>
                    </div>
                    <div className=" hidden Qsmd:flex flex-row  ">
                      <div className=" h-positive-negative-btn-height"></div>
                      <TopNavSmallButton
                        isDisabled={popupMode}
                        btnText={
                          mapChannelName(getSpecificChannelData?.provider) ||
                          TEXT?.N_A
                        }
                        handleFunction={handleTargetPopup}
                        icon={mapChannelIcon(getSpecificChannelData?.provider)}
                        enabledBgColor={"var(--primary-nav-act)"}
                        hoverOption={"white"}
                        enabledTextColor={"var(--text-primary)"}
                        disabledTextColor={"var(--text-primary)"}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* ---- Mobile-only "Quick Actions" panel: duplicates the desktop
                    action buttons above for small screens. ---- */}
              <div className="block Qsmd:hidden mt-margin-md">
                <p className="bg-primary-card text-sm py-padding-sm font-semibold text-text-primary pl-padding-md flex items-center">
                  Quick Actions
                </p>
                <div className="w-full bg-primary-nav-act flex flex-col border-t border-primary-bg">
                  {/* Row: Test notification / Edit channel */}
                  <div className="flex">
                    <div
                      className={`flex justify-center items-center  bg-primary-nav-act w-width-full h-[var(--topnav-small-btn-width)] }
                      }`}
                    >
                      <TopNavSmallButton
                        fullWidthRequired={true}
                        imgColor="var(--text-primary)"
                        btnText={
                          <>
                            {TEXT?.TEST} <br />{" "}
                            {TEXT?.NOTIFICATION.toLowerCase()}
                          </>
                        }
                        icon={ICONS?.TEST_NOTIFICATION}
                        hoverOption={"white"}
                        enabledBgColor={"var(--primary-nav-act)"}
                        enabledTextColor={"var(--text-primary)"}
                        disabledBgColor={"var(--primary-nav-act)"}
                        disabledTextColor={"var(--text-primary)"}
                        isDisabled={
                          !services?.includes("CChannel") ||
                          !getSpecificChannelData?.is_active ||
                          testNotificationBtnDisable ||
                          popupMode
                        }
                        handleFunction={handleTestNotification}
                      />
                    </div>
                    <div className="border-r  border-primary-bg"></div>
                    <div className="flex w-full justify-center items-center">
                      <TopNavSmallButton
                        fullWidthRequired={true}
                        btnText={
                          <>
                            {TEXT?.EDIT} <br /> {TEXT?.CHANNEL.toLowerCase()}
                          </>
                        }
                        icon={ICONS?.EDIT_WITH_CIRCLE}
                        hoverOption={"white"}
                        enabledBgColor={"var(--primary-nav-act)"}
                        imgColor={"var(--text-primary)"}
                        enabledTextColor={"var(--text-primary)"}
                        disabledBgColor={"var(--primary-nav-act)"}
                        disabledTextColor={"var(--text-primary)"}
                        isDisabled={!hasServices || popupMode}
                        handleFunction={handleEditChannel}
                      />
                    </div>
                  </div>

                  {/* Row: view notification channel button / Provider target button */}
                  <div className="border-b  border-primary-bg"></div>

                  <div className="flex">
                    <div
                      className={`flex  justify-center items-center  bg-primary-nav-act w-full h-[var(--topnav-small-btn-width)] `}
                    >
                      <div
                        className={`flex justify-center items-center   w-full  h-[var(--topnav-small-btn-width)] `}
                      >
                        {/* Row: View channel notifications button */}

                        <TopNavSmallButton
                          fullWidthRequired={true}
                          btnText={
                            <>
                              {TEXT?.CHANNEL} <br />{" "}
                              {TEXT?.NOTIFICATION.toLowerCase()}s
                            </>
                          }
                          icon={ICONS?.NOTIFICATION_WITH_WHITE_CIRCLE}
                          hoverOption={"white"}
                          enabledBgColor={"var(--primary-nav-act)"}
                          enabledTextColor={"var(--text-primary)"}
                          disabledBgColor={"var(--primary-nav-act)"}
                          disabledTextColor={"var(--text-primary)"}
                          isDisabled={
                            !canUseNotifyService ||
                            getSpecificChannelData?.verification_status ===
                              "pending" ||
                            popupMode
                          }
                          imgColor={"var(--text-primary)"}
                          handleFunction={handleNotificationViewAllPopup}
                        />
                      </div>
                    </div>
                    <div className="border-r  border-primary-bg"></div>
                    <div className="flex w-full justify-center items-center">
                      <TopNavSmallButton
                        isDisabled={popupMode}
                        fullWidthRequired={true}
                        btnText={
                          mapChannelName(getSpecificChannelData?.provider) ||
                          TEXT?.N_A
                        }
                        handleFunction={handleTargetPopup}
                        icon={mapChannelIcon(getSpecificChannelData?.provider)}
                        enabledBgColor={"var(--primary-nav-act)"}
                        hoverOption={"white"}
                        enabledTextColor={"var(--text-primary)"}
                        disabledTextColor={"var(--text-primary)"}
                      />
                    </div>
                  </div>

                  <div className="border-b  border-primary-bg"></div>

                  <div className="flex">
                    <div
                      className={`flex justify-center items-center bg-primary-nav-act w-full h-[var(--topnav-small-btn-width)] `}
                    >
                      {/* activate deactivate buttons */}

                      <TopNavSmallButton
                        fullWidthRequired={true}
                        btnText={
                          <>
                            {getSpecificChannelData?.is_active
                              ? TEXT?.DEACTIVATE
                              : TEXT?.ACTIVATE}
                            <br /> {TEXT?.CHANNEL.toLowerCase()}
                          </>
                        }
                        icon={
                          getSpecificChannelData?.is_active
                            ? ICONS?.REJECT_ICON_RED_WITH_WHITE_CIRCLE
                            : ICONS?.APPROVE_CIRCLE_GREEN_CHECKMARK
                        }
                        hoverOption={"white"}
                        enabledTextColor={"var(--text-primary)"}
                        disabledTextColor={"var(--text-primary)"}
                        disabledBgColor={"var(--primary-nav-act)"}
                        isDisabled={!hasServices || isLoading || popupMode}
                        handleFunction={handleChannelActivation}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* ---- Tabbed content: Details vs Channel notifications ---- */}
              <div className="device-details-div-section w-screen flex justify-center  h-auto  ">
                <div className="device-details-section  lg:px-padding-base md:px-padding-md  Qmd:pb-padding-base px-padding-md  w-full flex flex-col">
                  {/* Tab switcher control. */}
                  <div className="mt-margin-md Qsmd:mt-margin-md flex">
                    <div className="flex gap-gap-sm bg-primary-card w-auto lg:ml-margin-base rounded-border-radius-md">
                      <SwitcherTabs
                        initialActiveTab={TEXT?.DETAILS.toLowerCase()}
                        tabs={[
                          {
                            label: TEXT?.DETAILS,
                            key: "details",
                            handler: toggleDetails,
                          },
                        ]}
                        setIndex={setIndex}
                      />
                    </div>
                  </div>

                  {/* --- "Details" tab content: Origin + Support info cards --- */}
                  {index === GlobalConfiguration.lengthCheckZero && (
                    <div className={animationClass}>
                      <div
                        className={`flex flex-col lg:flex-row my-margin-md  gap-gap-md  `}
                      >
                        <CommonDetailCard
                          cardTitle={TEXT?.ORIGIN_DETAILS}
                          numCols={GlobalConfiguration?.lengthCheckOne}
                          variant="third"
                          entries={originItemList}
                        />

                        <CommonDetailCard
                          cardTitle={TEXT?.SUPPORT_INFORMATION}
                          numCols={GlobalConfiguration?.lengthCheckOne}
                          variant="third"
                          entries={supportItemList}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* Bottom spacer reserving room for the fixed pagination bar, shown
              only while on the notifications tab. */}
          <div
            className={`${index === GlobalConfiguration.lengthCheckZero ? "hidden" : ""}  h-pagination-height flex-shrink-0`}
          ></div>
        </>
      )}
    </div>
  );
};

export default ChannelDetails;
